export const extractBlock = (lines, startIndex) => {
    const startLine = lines[startIndex].trim();
    let endPattern;
    
    if (startLine.startsWith('```')) {
      endPattern = '```';
    } else if (/^:::(\s|\w)+/.test(startLine)) {
      endPattern = ':::';
    } else {
      return { content: [lines[startIndex]], endIndex: startIndex };
    }
    
    const blockContent = [lines[startIndex]];
    let endIndex = startIndex;
    
    for (let i = startIndex+1 ; i < lines.length; i++) {
      blockContent.push(lines[i]);
      if (lines[i].trim() === endPattern) {
        endIndex = i;
        break;
      }
    }
    
    return { content: blockContent, endIndex };
  };

  // Function to identify special blocks like mermaid or excalidraw
export const isSpecialBlock = (line) => {
    const trimmed = line.trim();
    return (
      trimmed.startsWith('```mermaid') || 
      trimmed.startsWith('```excalidraw') ||
      trimmed.startsWith('```table') ||
      /^:::(\s|\w)+/.test(trimmed) // For block container syntax
    );
  };

  export const isUnknownBlock = (line) => {
    const trimmed = line.trim();
    return (
      !isSpecialBlock(trimmed) &&
      isBlock(trimmed)
    );
  };
  export const isBlockNonLanguage = (line) => {
    const trimmed = line.trim();
    return (
      trimmed==='```' // For block container syntax
    );
  };
  export const isBlock = (line) => {
    const trimmed = line.trim();
    return (
      trimmed.startsWith('```') ||
      /^:::(\s|\w)+/.test(trimmed) // For block container syntax
    );
  };



// Helper function to process nested list indentation
export const processMarkdownIndentation = (markdown) => {
  if (!markdown) return markdown;
  
  // Split into lines to process
  const lines = markdown.split('\n');
  const processedLines = [];
  
  // More robust pattern matching for lists
  const orderedListRegex = /^(\s*)(\d+)\.(.*)$/;
  const unorderedListRegex = /^(\s*)[*+-](.*)$/;
  
  let currentIndentLevel = 0;
  let prevParentLine = -1; // Track the line number of the parent list item
  let prevIndentLevel = 0;
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trimRight(); // Keep left indentation
    
    // Check for ordered lists with any amount of leading whitespace
    const olMatch = line.match(orderedListRegex);
    // Check for unordered lists with any amount of leading whitespace
    const ulMatch = line.match(unorderedListRegex);
    
    if (olMatch) {
      // Ordered list item
      const [fullMatch, indent, number, content] = olMatch;
      // Convert to standard increments (3 spaces per level)
      const indentLevel = Math.max(1, Math.ceil(indent.length / 3));
      
      // For nested items, ensure proper indentation
      if (indentLevel > 1) {
        // This is a nested item - use a standard indent based on level
        const standardIndent = '   '.repeat(indentLevel); // 3 spaces per level
        processedLines.push(`${standardIndent}${number}.${content}`);
      } else {
        // Top level item - keep as is
        processedLines.push(line);
      }
      
      prevIndentLevel = indentLevel;
      
      // Mark this line as a potential parent if the next item is more indented
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1].trimRight();
        const nextOlMatch = nextLine.match(orderedListRegex);
        const nextUlMatch = nextLine.match(unorderedListRegex);
        
        if (nextOlMatch) {
          const [, nextIndent] = nextOlMatch;
          if (nextIndent.length > indent.length) {
            prevParentLine = i;
          }
        } else if (nextUlMatch) {
          const [, nextIndent] = nextUlMatch;
          if (nextIndent.length > indent.length) {
            prevParentLine = i;
          }
        }
      }
    } else if (ulMatch) {
      // Unordered list item
      const [fullMatch, indent, content] = ulMatch;
      // Convert to standard increments (3 spaces per level)
      const indentLevel = Math.max(1, Math.ceil(indent.length / 3));
      
      // For nested items, ensure proper indentation
      if (indentLevel > 1) {
        // This is a nested item - use a standard indent based on level
        const standardIndent = '   '.repeat(indentLevel); // 3 spaces per level
        const bullet = line.trim()[0]; // Preserve the original bullet character
        processedLines.push(`${standardIndent}${bullet}${content}`);
      } else {
        // Top level item - keep as is
        processedLines.push(line);
      }
      
      prevIndentLevel = indentLevel;
      
      // Mark this line as a potential parent if the next item is more indented
      if (i + 1 < lines.length) {
        const nextLine = lines[i + 1].trimRight();
        const nextOlMatch = nextLine.match(orderedListRegex);
        const nextUlMatch = nextLine.match(unorderedListRegex);
        
        if (nextOlMatch) {
          const [, nextIndent] = nextOlMatch;
          if (nextIndent.length > indent.length) {
            prevParentLine = i;
          }
        } else if (nextUlMatch) {
          const [, nextIndent] = nextUlMatch;
          if (nextIndent.length > indent.length) {
            prevParentLine = i;
          }
        }
      }
    } else {
      // Not a list item
      processedLines.push(line);
    }
  }
  
  return processedLines.join('\n');
};

// Helper function to determine if content is a table block
export const isTableBlock = (content) => {
  if (!content || !content[0]) return false;
  const firstLine = content[0].trim();
  return firstLine.startsWith('```table');
};

// Helper function to determine if content is an excalidraw block
export const isExcalidrawBlock = (content) => {
  if (!content || !content[0]) return false;
  const firstLine = content[0].trim();
  return firstLine.startsWith('```excalidraw');
};

// Helper function to determine if content is a mermaid block
export const isMermaidBlock = (content) => {
  if (Array.isArray(content)) {
    return content.length > 0 && content[0].trim().startsWith('```mermaid');
  }
  return typeof content === 'string' && content.trim().startsWith('```mermaid');
};

// Function to detect if content is a list item (bullet or numbered list)
export const isListItemBlock = (content) => {
  if (Array.isArray(content)) {
    // Check if the first line starts with a list marker
    return content.length > 0 && (
      /^[-*+]\s/.test(content[0].trim()) ||  // Bullet list
      /^\d+\.\s/.test(content[0].trim())     // Numbered list
    );
  }
  
  // If it's a string, check the first line
  return typeof content === 'string' && (
    /^[-*+]\s/.test(content.trim()) ||  // Bullet list
    /^\d+\.\s/.test(content.trim())     // Numbered list
  );
};

export const getBlockType = (content) => {
  if (isTableBlock(content)) return 'table';
  if (isExcalidrawBlock(content)) return 'excalidraw';
  if (isMermaidBlock(content)) return 'mermaid';
  if (isListItemBlock(content)) return 'list'; // Add list detection
  return 'unknown';
};